import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import IconArrowLeftWhite from 'components/icons/Slider/iconArrowLeftWhite';
import IconArrowRightWhite from 'components/icons/Slider/iconArrowRightWhite';
import VisuallyHidden from 'components/consumer/VisuallyHidden';

const PDPDimensionShotViewer = ({
  currentDimensionShot,
  hasMultipleDimensionShots,
  selectedDimensionShotIndex,
  totalDimensionShots,
  onPrevious,
  onNext,
  className,
  ...imageProps
}) => {
  const [imageSrc, setImageSrc] = useState(currentDimensionShot?.src);
  const [hasErrored, setHasErrored] = useState(false);

  // Reset state when currentDimensionShot changes
  useEffect(() => {
    setImageSrc(currentDimensionShot?.src);
    setHasErrored(false);
  }, [currentDimensionShot?.src]);

  const handleImageError = () => {
    if (!hasErrored && currentDimensionShot?.baseUrl) {
      setImageSrc(currentDimensionShot.baseUrl);
      setHasErrored(true);
    }
  };

  // Keyboard navigation support
  useEffect(() => {
    if (!hasMultipleDimensionShots) return undefined;

    const handleKeyDown = event => {
      if (event.key === 'ArrowLeft') {
        onPrevious?.();
      } else if (event.key === 'ArrowRight') {
        onNext?.();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [hasMultipleDimensionShots, onPrevious, onNext]);

  // Extract and filter out invalid DOM props
  const { enableCrops, crops, type, lazy, ...validImgProps } = imageProps || {};

  return (
    <div className={classNames('relative w-full h-full', className)}>
      {currentDimensionShot && (
        <img
          {...validImgProps}
          src={imageSrc}
          alt={`Product dimensions - ${currentDimensionShot.dimensionCode}`}
          className="h-full w-full object-contain object-center select-none"
          onError={handleImageError}
          loading={typeof lazy === 'boolean' ? (lazy ? 'lazy' : 'eager') : lazy}
        />
      )}

      {hasMultipleDimensionShots && (
        <>
          <button
            type="button"
            onClick={onPrevious}
            className="group/SliderButton absolute -left-3 md:left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex justify-center items-center bg-white border border-gray-light6 transition-all duration-200 hover:border-brand [&_svg_path]:fill-gray [&_svg_path]:transition-all [&_svg_path]:duration-200 group-hover/SliderButton:[&_svg_path]:fill-[#2f2f2e]"
            aria-label="Previous image"
          >
            <IconArrowLeftWhite width={20} height={20} />
            <VisuallyHidden>Previous image</VisuallyHidden>
          </button>

          <button
            type="button"
            onClick={onNext}
            className="group/SliderButton absolute -right-3 md:right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex justify-center items-center bg-white border border-gray-light6 transition-all duration-200 hover:border-brand [&_svg_path]:fill-gray [&_svg_path]:transition-all [&_svg_path]:duration-200 group-hover/SliderButton:[&_svg_path]:fill-[#2f2f2e]"
            aria-label="Next image"
          >
            <IconArrowRightWhite width={20} height={20} />
            <VisuallyHidden>Next image</VisuallyHidden>
          </button>
        </>
      )}
    </div>
  );
};

PDPDimensionShotViewer.propTypes = {
  currentDimensionShot: PropTypes.shape({
    src: PropTypes.string.isRequired,
    baseUrl: PropTypes.string,
    dimensionCode: PropTypes.string.isRequired,
  }),
  hasMultipleDimensionShots: PropTypes.bool,
  selectedDimensionShotIndex: PropTypes.number,
  totalDimensionShots: PropTypes.number,
  onPrevious: PropTypes.func,
  onNext: PropTypes.func,
  className: PropTypes.string,
};

export default PDPDimensionShotViewer;
